import React, { useEffect, useState, useCallback } from 'react';

// Email / SMTP configuration — Reg 8649.
// Rendered as the "Email / SMTP" tab inside SMS / Email Settings.
// Master / Super Admin (system role 'admin'): one global SMTP account plus an
// Enable / Disable switch per property. Everyone else (Hotel Admin included)
// sees a read-only status for their property and its send log — the API
// enforces the same split.

const emptyForm = () => ({
  smtp_host: '',
  smtp_port: 587,
  smtp_secure: false,
  smtp_user: '',
  smtp_pass: '',
  from_name: '',
  from_email: '',
  reply_to: '',
  is_active: true,
});

const lbl = { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: '#475569', marginBottom: '0.2rem', textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' };
const th = { padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' };
const td = { padding: '0.35rem 0.5rem', verticalAlign: 'middle' };
const errBox = { background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' };
const okBox = { background: '#f0fdf4', border: '1px solid #86efac', color: '#15803d', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' };
const h3 = { margin: '0 0 0.4rem', fontSize: '1rem', color: '#1e3a8a' };

function EmailLogTable({ logs, showProperty }) {
  if (!logs.length) return <div style={{ color: '#94a3b8', fontSize: '0.82rem' }}>No emails sent yet.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
        <thead>
          <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
            <th style={th}>Time</th>
            {showProperty && <th style={th}>Property</th>}
            <th style={th}>To</th>
            <th style={th}>Type</th>
            <th style={th}>Subject</th>
            <th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(l.created_at).toLocaleString()}</td>
              {showProperty && <td style={td}>{l.property_name || '—'}</td>}
              <td style={td}>{l.to_email}</td>
              <td style={td}>{l.doc_type || '-'}</td>
              <td style={td}>{l.subject_snippet}</td>
              <td style={{ ...td, color: l.success ? '#15803d' : '#b91c1c' }}>
                {l.success ? '✓ Sent' : `✕ ${l.error_message || 'Failed'}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EmailProviderSettings({ token, selectedProperty, role }) {
  const isPlatformAdmin = role === 'admin';

  const headers = useCallback((extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra,
  }), [token, selectedProperty]);

  return isPlatformAdmin
    ? <GlobalEmailSettings headers={headers} />
    : <PropertyEmailStatus headers={headers} selectedProperty={selectedProperty} />;
}

// ── Property users: read-only ────────────────────────────────────────────
function PropertyEmailStatus({ headers, selectedProperty }) {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedProperty?.id) return;
    setError('');
    fetch('/api/v1/email/status', { headers: headers() })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Failed to load email status (HTTP ${res.status})`);
        setStatus(data);
      })
      .catch((err) => setError(err.message));
    fetch('/api/v1/email/logs', { headers: headers() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setLogs(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [headers, selectedProperty]);

  if (!selectedProperty?.id) {
    return <div style={{ padding: '1rem', color: '#64748b' }}>Select a property to see its email status.</div>;
  }

  const on = status?.email_enabled && status?.smtp_configured;
  return (
    <div style={{ maxWidth: 760 }}>
      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
        Email (SMTP) is managed centrally by the Platform Admin. Invoices, quotations, the guest-portal link,
        booking confirmations and the Night Audit report are emailed only when email is enabled for this property.
      </p>
      {error && <div style={errBox}>{error}</div>}
      {status && (
        <div style={{ ...(on ? okBox : errBox), fontSize: '0.9rem' }}>
          {on
            ? <>Email is <strong>enabled</strong> for this property{status.from_email ? <> — sent from <strong>{status.from_name ? `${status.from_name} <${status.from_email}>` : status.from_email}</strong></> : null}.</>
            : status.email_enabled
              ? <>Email is enabled for this property, but the global SMTP account has not been set up yet. Contact the Platform Admin.</>
              : <>Email is <strong>disabled</strong> for this property. Contact the Platform Admin to enable it.</>}
        </div>
      )}
      <h4 style={{ margin: '1.25rem 0 0.5rem', fontSize: '0.9rem', color: '#1e293b' }}>Recent emails</h4>
      <EmailLogTable logs={logs} showProperty={false} />
    </div>
  );
}

// ── Master / Super Admin: global SMTP + per-property switch ─────────────
function GlobalEmailSettings({ headers }) {
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [passSet, setPassSet] = useState(false);
  const [passEditing, setPassEditing] = useState(true);
  const [props, setProps] = useState([]);
  const [propFilter, setPropFilter] = useState('');
  const [rowBusy, setRowBusy] = useState(null);
  const [propMsg, setPropMsg] = useState('');
  const [propErr, setPropErr] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testProperty, setTestProperty] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const applyConfig = (data) => {
    if (data) {
      setForm({
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        smtp_secure: data.smtp_secure === true,
        smtp_user: data.smtp_user || '',
        smtp_pass: '',
        from_name: data.from_name || '',
        from_email: data.from_email || '',
        reply_to: data.reply_to || '',
        is_active: data.is_active !== false,
      });
      setPassSet(!!data.smtp_pass_set);
      setPassEditing(!data.smtp_pass_set);
    } else {
      setForm(emptyForm());
      setPassSet(false);
      setPassEditing(true);
    }
  };

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/email/global-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed to load email settings (HTTP ${res.status})`);
      applyConfig(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadProps = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/email/properties', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed to load properties (HTTP ${res.status})`);
      setProps((Array.isArray(data) ? data : []).map((p) => ({ ...p, from_name: p.from_name || '', reply_to: p.reply_to || '' })));
    } catch (err) {
      setPropErr(err.message);
    }
  }, [headers]);

  const loadLogs = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/email/logs?property_id=all', { headers: headers() });
      const data = await res.json().catch(() => []);
      if (res.ok) setLogs(Array.isArray(data) ? data : []);
    } catch { /* non-critical */ }
  }, [headers]);

  useEffect(() => { loadConfig(); loadProps(); loadLogs(); }, [loadConfig, loadProps, loadLogs]);

  const upd = (patch) => setForm((p) => ({ ...p, ...patch }));

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const body = { ...form };
      // Only send the password when a new one was actually entered.
      if (!passEditing || !form.smtp_pass) delete body.smtp_pass;
      const res = await fetch('/api/v1/email/global-config', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      applyConfig(data);
      setMessage('Global SMTP settings saved. All enabled properties now send through this account.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updRow = (propertyId, patch) => setProps((list) => list.map((p) => (p.property_id === propertyId ? { ...p, ...patch, dirty: true } : p)));

  const saveRow = async (row, override = {}) => {
    const next = { ...row, ...override };
    setRowBusy(row.property_id);
    setPropErr('');
    setPropMsg('');
    try {
      const res = await fetch(`/api/v1/email/properties/${row.property_id}`, {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ email_enabled: next.email_enabled, from_name: next.from_name, reply_to: next.reply_to }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      setProps((list) => list.map((p) => (p.property_id === row.property_id
        ? { ...p, email_enabled: data.email_enabled, from_name: data.from_name || '', reply_to: data.reply_to || '', updated_at: data.updated_at, updated_by: data.updated_by, dirty: false }
        : p)));
      setPropMsg(`${row.property_name}: email ${data.email_enabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      setPropErr(`${row.property_name}: ${err.message}`);
    } finally {
      setRowBusy(null);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { setTestResult({ ok: false, msg: 'Enter a recipient email' }); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/v1/email/test', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ to: testTo.trim(), property_id: testProperty || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Test failed');
      setTestResult({ ok: true, msg: `Sent to ${testTo.trim()}` });
      loadLogs();
    } catch (err) {
      setTestResult({ ok: false, msg: err.message });
    } finally {
      setTesting(false);
    }
  };

  const q = propFilter.trim().toLowerCase();
  const shownProps = q
    ? props.filter((p) => `${p.property_name} ${p.property_code} ${p.chain_name}`.toLowerCase().includes(q))
    : props;
  const enabledCount = props.filter((p) => p.email_enabled).length;

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Global SMTP */}
      <div style={{ maxWidth: 760 }}>
        <h3 style={h3}>Global SMTP account</h3>
        <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 1rem' }}>
          One SMTP account for all properties. Only Master / Super Admin can view or change it — property users,
          including Hotel Admin, only see whether email is enabled for their property.
        </p>

        {error && <div style={errBox}>{error}</div>}
        {message && <div style={okBox}>{message}</div>}

        {loading ? <div style={{ color: '#64748b' }}>Loading…</div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.8rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>SMTP Host *</label>
              <input style={inp} value={form.smtp_host} placeholder="smtp.yourdomain.com" onChange={(e) => upd({ smtp_host: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>SMTP Port *</label>
              <input style={inp} type="number" value={form.smtp_port} placeholder="587" onChange={(e) => upd({ smtp_port: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>Encryption</label>
              <select style={inp} value={form.smtp_secure ? 'ssl' : 'starttls'} onChange={(e) => upd({ smtp_secure: e.target.value === 'ssl' })}>
                <option value="starttls">STARTTLS (port 587)</option>
                <option value="ssl">SSL/TLS (port 465)</option>
              </select>
            </div>
            <div>
              <label style={lbl}>SMTP Username</label>
              <input style={inp} value={form.smtp_user} placeholder="noreply@yourdomain.com" onChange={(e) => upd({ smtp_user: e.target.value })} autoComplete="off" />
            </div>
            <div>
              <label style={lbl}>SMTP Password</label>
              {passSet && !passEditing ? (
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input style={{ ...inp, background: '#f8fafc', color: '#94a3b8' }} value="•••••••• (saved)" readOnly />
                  <button className="btn-muted btn-sm" type="button" onClick={() => { setPassEditing(true); upd({ smtp_pass: '' }); }}>Change</button>
                </div>
              ) : (
                <input style={inp} type="password" value={form.smtp_pass} placeholder="SMTP password" onChange={(e) => upd({ smtp_pass: e.target.value })} autoComplete="new-password" />
              )}
            </div>
            <div>
              <label style={lbl}>Default From Name</label>
              <input style={inp} value={form.from_name} placeholder="EASEPMS" onChange={(e) => upd({ from_name: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>From Email *</label>
              <input style={inp} value={form.from_email} placeholder="noreply@yourdomain.com" onChange={(e) => upd({ from_email: e.target.value })} />
            </div>
            <div>
              <label style={lbl}>Default Reply-To (optional)</label>
              <input style={inp} value={form.reply_to} placeholder="support@yourdomain.com" onChange={(e) => upd({ reply_to: e.target.value })} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '1.2rem' }}>
              <input id="email-global-active" type="checkbox" checked={form.is_active} onChange={(e) => upd({ is_active: e.target.checked })} />
              <label htmlFor="email-global-active" style={{ fontSize: '0.85rem', color: '#334155' }}>Active</label>
            </div>
            <div style={{ gridColumn: '1 / -1', marginTop: '0.4rem' }}>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Global SMTP'}</button>
            </div>
          </div>
        )}

        {/* Test email */}
        <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
          <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#1e293b' }}>Send a test email</h4>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input style={{ ...inp, maxWidth: 260 }} value={testTo} placeholder="recipient@example.com" onChange={(e) => setTestTo(e.target.value)} />
            <select style={{ ...inp, maxWidth: 260 }} value={testProperty} onChange={(e) => setTestProperty(e.target.value)}>
              <option value="">Global SMTP (no property)</option>
              {props.map((p) => <option key={p.property_id} value={p.property_id}>As {p.property_name}</option>)}
            </select>
            <button className="btn-success btn-sm" onClick={sendTest} disabled={testing}>{testing ? 'Sending…' : 'Send Test'}</button>
            {testResult && (
              <span style={{ fontSize: '0.82rem', color: testResult.ok ? '#15803d' : '#b91c1c' }}>
                {testResult.ok ? '✓ ' : '✕ '}{testResult.msg}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.35rem' }}>
            Save the settings before testing. Testing "as" a property applies its Enable switch and From Name.
          </div>
        </div>
      </div>

      {/* Per-property switch */}
      <div style={{ marginTop: '1.75rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <div>
            <h3 style={h3}>Email by property</h3>
            <div style={{ color: '#64748b', fontSize: '0.82rem' }}>
              Enabled for {enabledCount} of {props.length} properties. Disabled properties send no email at all.
            </div>
          </div>
          <input style={{ ...inp, maxWidth: 260 }} value={propFilter} placeholder="Search property / chain" onChange={(e) => setPropFilter(e.target.value)} />
        </div>
        {propErr && <div style={errBox}>{propErr}</div>}
        {propMsg && <div style={okBox}>{propMsg}</div>}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                <th style={th}>Property</th>
                <th style={th}>Chain</th>
                <th style={th}>Email</th>
                <th style={th}>From Name (optional)</th>
                <th style={th}>Reply-To (optional)</th>
                <th style={th}>Last email</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {shownProps.map((p) => (
                <tr key={p.property_id} style={{ borderBottom: '1px solid #e2e8f0', opacity: p.property_active === false ? 0.6 : 1 }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{p.property_name}</div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      {p.property_code}{p.has_own_smtp ? ' · has its old own SMTP' : ''}
                    </div>
                  </td>
                  <td style={td}>{p.chain_name || '—'}</td>
                  <td style={td}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={!!p.email_enabled} disabled={rowBusy === p.property_id}
                        onChange={(e) => saveRow(p, { email_enabled: e.target.checked })} />
                      <span style={{ fontWeight: 700, color: p.email_enabled ? '#15803d' : '#94a3b8' }}>{p.email_enabled ? 'Enabled' : 'Disabled'}</span>
                    </label>
                  </td>
                  <td style={td}>
                    <input style={{ ...inp, minWidth: 150 }} value={p.from_name} placeholder={form.from_name || p.property_name}
                      onChange={(e) => updRow(p.property_id, { from_name: e.target.value })} />
                  </td>
                  <td style={td}>
                    <input style={{ ...inp, minWidth: 170 }} value={p.reply_to} placeholder={form.reply_to || 'frontdesk@hotel.com'}
                      onChange={(e) => updRow(p.property_id, { reply_to: e.target.value })} />
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '0.75rem', color: p.last_sent_at ? (p.last_success ? '#15803d' : '#b91c1c') : '#94a3b8' }}>
                    {p.last_sent_at ? `${p.last_success ? '✓' : '✕'} ${new Date(p.last_sent_at).toLocaleString()}` : '—'}
                  </td>
                  <td style={td}>
                    {p.dirty && (
                      <button className="btn-primary btn-sm" disabled={rowBusy === p.property_id} onClick={() => saveRow(p)}>
                        {rowBusy === p.property_id ? 'Saving…' : 'Save'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!shownProps.length && (
                <tr><td colSpan={7} style={{ ...td, color: '#94a3b8', textAlign: 'center', padding: '1rem' }}>No properties found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent send log, all properties */}
      <div style={{ marginTop: '1.75rem' }}>
        <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#1e293b' }}>Recent emails (all properties)</h4>
        <EmailLogTable logs={logs} showProperty />
      </div>
    </div>
  );
}
